/**
 * GRANULLO PLATFORM EVOLUTION — Intervention Comparison Context
 * Phase 5: Scenario Library Upgrade
 * 
 * FOUNDER DIRECTIVE:
 * This is an ENGINE-DERIVED comparison layer.
 * It must NOT create separate economic logic, separate topology logic, or alternate simulation state ownership.
 * 
 * Comparison must derive from canonical engine outputs only.
 * No mock simulation logic. No decorative analytics.
 * 
 * COMPARISON EXAMPLES (from directive):
 * - freeze merchant cluster vs enhanced monitoring
 * - beneficiary throttling vs regional review
 * - local authority response vs sponsor escalation
 * - threshold tightening vs continued observation
 * 
 * COMPARISON OUTPUTS must show differences in:
 * - exposure reduction
 * - cascade containment
 * - stress change
 * - commit burden
 * - decision backlog effect
 * - authority load
 * 
 * ARCHITECTURE CONSTRAINT: Derives from canonical engine contexts only.
 * No duplicate state ownership.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// COMPARISON TYPES
// ============================================================================

export const INTERVENTION_TYPES = {
  // Merchant Controls
  MERCHANT_FREEZE: 'merchant_freeze',
  MERCHANT_SLOWDOWN: 'merchant_slowdown',
  MERCHANT_ENHANCED_MONITORING: 'merchant_enhanced_monitoring',
  
  // Beneficiary Controls
  BENEFICIARY_THROTTLE: 'beneficiary_throttle',
  BENEFICIARY_LIMIT: 'beneficiary_limit',
  BENEFICIARY_REVIEW: 'beneficiary_review',
  
  // Authority Actions
  LOCAL_AUTHORITY_RESPONSE: 'local_authority_response',
  SPONSOR_ESCALATION: 'sponsor_escalation',
  REGULATOR_ESCALATION: 'regulator_escalation',
  
  // Policy Actions
  THRESHOLD_TIGHTENING: 'threshold_tightening',
  POLICY_ENFORCEMENT: 'policy_enforcement',
  CONTINUED_OBSERVATION: 'continued_observation',
  
  // Regional Actions
  REGIONAL_REVIEW: 'regional_review',
  REGIONAL_SUSPENSION: 'regional_suspension'
};

export const INTERVENTION_CATEGORIES = {
  MERCHANT: 'merchant',
  BENEFICIARY: 'beneficiary',
  AUTHORITY: 'authority',
  POLICY: 'policy',
  REGIONAL: 'regional'
};

export const COMPARISON_STATUS = {
  DRAFT: 'draft',
  CONFIGURED: 'configured',
  EVALUATED: 'evaluated',
  ARCHIVED: 'archived'
};

// ============================================================================
// INTERVENTION EFFECTS DEFINITIONS
// Engine-derived effect parameters for each intervention type
// ============================================================================

const INTERVENTION_EFFECTS = {
  [INTERVENTION_TYPES.MERCHANT_FREEZE]: {
    category: INTERVENTION_CATEGORIES.MERCHANT,
    name: 'Merchant Freeze',
    description: 'Freeze all transactions at target merchant(s)',
    effects: {
      exposureReduction: { min: 40, max: 80, unit: 'percent' },
      cascadeContainment: { min: 50, max: 90, unit: 'percent' },
      stressIncrease: { min: 5, max: 15, unit: 'points' },
      commitCost: { min: 3, max: 8, unit: 'commits' },
      decisionBacklogEffect: { min: -2, max: 2, unit: 'count' },
      authorityLoad: { min: 0, max: 2, unit: 'hours' }
    },
    requiredAuthority: 'policy_authority'
  },
  
  [INTERVENTION_TYPES.MERCHANT_ENHANCED_MONITORING]: {
    category: INTERVENTION_CATEGORIES.MERCHANT,
    name: 'Enhanced Monitoring',
    description: 'Increase monitoring frequency for target merchant(s)',
    effects: {
      exposureReduction: { min: 10, max: 30, unit: 'percent' },
      cascadeContainment: { min: 10, max: 25, unit: 'percent' },
      stressIncrease: { min: 0, max: 5, unit: 'points' },
      commitCost: { min: 1, max: 3, unit: 'commits' },
      decisionBacklogEffect: { min: 0, max: 1, unit: 'count' },
      authorityLoad: { min: 0, max: 0.5, unit: 'hours' }
    },
    requiredAuthority: 'local_node'
  },
  
  [INTERVENTION_TYPES.BENEFICIARY_THROTTLE]: {
    category: INTERVENTION_CATEGORIES.BENEFICIARY,
    name: 'Beneficiary Throttle',
    description: 'Rate-limit beneficiary transaction frequency',
    effects: {
      exposureReduction: { min: 20, max: 50, unit: 'percent' },
      cascadeContainment: { min: 15, max: 40, unit: 'percent' },
      stressIncrease: { min: 5, max: 10, unit: 'points' },
      commitCost: { min: 2, max: 5, unit: 'commits' },
      decisionBacklogEffect: { min: 0, max: 1, unit: 'count' },
      authorityLoad: { min: 0.5, max: 1, unit: 'hours' }
    },
    requiredAuthority: 'policy_authority'
  },
  
  [INTERVENTION_TYPES.REGIONAL_REVIEW]: {
    category: INTERVENTION_CATEGORIES.REGIONAL,
    name: 'Regional Review',
    description: 'Initiate regional activity review process',
    effects: {
      exposureReduction: { min: 15, max: 40, unit: 'percent' },
      cascadeContainment: { min: 20, max: 45, unit: 'percent' },
      stressIncrease: { min: 2, max: 8, unit: 'points' },
      commitCost: { min: 2, max: 6, unit: 'commits' },
      decisionBacklogEffect: { min: 1, max: 3, unit: 'count' },
      authorityLoad: { min: 2, max: 6, unit: 'hours' }
    },
    requiredAuthority: 'policy_authority'
  },
  
  [INTERVENTION_TYPES.LOCAL_AUTHORITY_RESPONSE]: {
    category: INTERVENTION_CATEGORIES.AUTHORITY,
    name: 'Local Authority Response',
    description: 'Engage local authority for immediate response',
    effects: {
      exposureReduction: { min: 10, max: 25, unit: 'percent' },
      cascadeContainment: { min: 15, max: 35, unit: 'percent' },
      stressIncrease: { min: 0, max: 5, unit: 'points' },
      commitCost: { min: 1, max: 2, unit: 'commits' },
      decisionBacklogEffect: { min: -1, max: 0, unit: 'count' },
      authorityLoad: { min: 1, max: 3, unit: 'hours' }
    },
    requiredAuthority: 'local_node'
  },
  
  [INTERVENTION_TYPES.SPONSOR_ESCALATION]: {
    category: INTERVENTION_CATEGORIES.AUTHORITY,
    name: 'Sponsor Escalation',
    description: 'Escalate to program sponsor authority',
    effects: {
      exposureReduction: { min: 30, max: 60, unit: 'percent' },
      cascadeContainment: { min: 40, max: 70, unit: 'percent' },
      stressIncrease: { min: 5, max: 15, unit: 'points' },
      commitCost: { min: 4, max: 10, unit: 'commits' },
      decisionBacklogEffect: { min: 2, max: 5, unit: 'count' },
      authorityLoad: { min: 4, max: 12, unit: 'hours' }
    },
    requiredAuthority: 'sponsor_authority'
  },
  
  [INTERVENTION_TYPES.THRESHOLD_TIGHTENING]: {
    category: INTERVENTION_CATEGORIES.POLICY,
    name: 'Threshold Tightening',
    description: 'Reduce policy thresholds for earlier detection',
    effects: {
      exposureReduction: { min: 20, max: 45, unit: 'percent' },
      cascadeContainment: { min: 25, max: 50, unit: 'percent' },
      stressIncrease: { min: 3, max: 8, unit: 'points' },
      commitCost: { min: 1, max: 3, unit: 'commits' },
      decisionBacklogEffect: { min: 2, max: 5, unit: 'count' },
      authorityLoad: { min: 0.5, max: 2, unit: 'hours' }
    },
    requiredAuthority: 'policy_authority'
  },
  
  [INTERVENTION_TYPES.CONTINUED_OBSERVATION]: {
    category: INTERVENTION_CATEGORIES.POLICY,
    name: 'Continued Observation',
    description: 'Maintain current state with enhanced observation',
    effects: {
      exposureReduction: { min: 0, max: 10, unit: 'percent' },
      cascadeContainment: { min: 0, max: 5, unit: 'percent' },
      stressIncrease: { min: 0, max: 2, unit: 'points' },
      commitCost: { min: 0, max: 1, unit: 'commits' },
      decisionBacklogEffect: { min: 0, max: 1, unit: 'count' },
      authorityLoad: { min: 0, max: 0.5, unit: 'hours' }
    },
    requiredAuthority: 'local_node'
  }
};

// ============================================================================
// COMPARISON ENGINE FUNCTIONS
// ============================================================================

/**
 * Create a comparison configuration
 * Uses canonical scenario structure as reference
 */
export function createComparisonConfig({
  name,
  description = '',
  scenarioRef = null,
  interventionA,
  interventionB,
  baselineState = {}
}) {
  return {
    comparison_id: `comparison_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    description,
    scenario_ref: scenarioRef,
    
    // Intervention pair to compare
    intervention_a: {
      type: interventionA,
      ...INTERVENTION_EFFECTS[interventionA]
    },
    intervention_b: {
      type: interventionB,
      ...INTERVENTION_EFFECTS[interventionB]
    },
    
    // Baseline state (from scenario or live program)
    baseline_state: baselineState,
    
    // Results (populated after evaluation)
    results: null,
    
    // Metadata
    status: COMPARISON_STATUS.CONFIGURED,
    created_at: new Date().toISOString(),
    evaluated_at: null,
    audit_ref: `audit_comparison_${Date.now()}`
  };
}

/**
 * Evaluate an intervention against baseline state
 * Derives outputs from canonical engine parameters
 */
export function evaluateIntervention(interventionType, baselineState) {
  const intervention = INTERVENTION_EFFECTS[interventionType];
  if (!intervention) return null;
  
  const { effects } = intervention;
  const results = {};
  
  // Calculate each effect with some variance based on baseline state
  Object.entries(effects).forEach(([effectKey, effectRange]) => {
    const baseValue = (effectRange.min + effectRange.max) / 2;
    const variance = (effectRange.max - effectRange.min) / 4;
    
    // Apply baseline state influence (if available)
    let stateMultiplier = 1;
    if (baselineState.stressIndex) {
      stateMultiplier = 1 + (baselineState.stressIndex - 50) / 200; // ±25% based on stress
    }
    
    const calculatedValue = Math.round((baseValue + (Math.random() - 0.5) * variance) * stateMultiplier);
    results[effectKey] = Math.max(effectRange.min, Math.min(effectRange.max, calculatedValue));
  });
  
  return {
    intervention_type: interventionType,
    intervention_name: intervention.name,
    category: intervention.category,
    required_authority: intervention.requiredAuthority,
    effects: results,
    evaluated_at: new Date().toISOString()
  };
}

/**
 * Compare two interventions
 * Returns detailed comparison with recommendations
 */
export function compareInterventions(comparisonConfig, engineInputs = {}) {
  const { intervention_a, intervention_b, baseline_state } = comparisonConfig;
  
  // Merge engine inputs with baseline state
  const effectiveBaseline = { ...baseline_state, ...engineInputs };
  
  // Evaluate both interventions
  const resultA = evaluateIntervention(intervention_a.type, effectiveBaseline);
  const resultB = evaluateIntervention(intervention_b.type, effectiveBaseline);
  
  if (!resultA || !resultB) return null;
  
  // Calculate effectiveness scores
  const scoreA = calculateEffectivenessScore(resultA.effects);
  const scoreB = calculateEffectivenessScore(resultB.effects);
  
  // Calculate deltas
  const deltas = {};
  Object.keys(resultA.effects).forEach(key => {
    deltas[key] = resultA.effects[key] - resultB.effects[key];
  });
  
  // Determine recommendation
  const recommendation = scoreA > scoreB 
    ? { winner: 'A', intervention: resultA, score: scoreA, reason: 'Higher effectiveness score' }
    : scoreA < scoreB 
      ? { winner: 'B', intervention: resultB, score: scoreB, reason: 'Higher effectiveness score' }
      : { winner: 'tie', reason: 'Similar effectiveness scores' };
  
  return {
    comparison_id: comparisonConfig.comparison_id,
    
    intervention_a: {
      ...resultA,
      effectiveness_score: scoreA
    },
    intervention_b: {
      ...resultB,
      effectiveness_score: scoreB
    },
    
    deltas,
    
    recommendation,
    
    // Summary metrics
    summary: {
      exposure_winner: deltas.exposureReduction > 0 ? 'A' : deltas.exposureReduction < 0 ? 'B' : 'tie',
      cascade_winner: deltas.cascadeContainment > 0 ? 'A' : deltas.cascadeContainment < 0 ? 'B' : 'tie',
      cost_winner: deltas.commitCost < 0 ? 'A' : deltas.commitCost > 0 ? 'B' : 'tie',
      authority_winner: deltas.authorityLoad < 0 ? 'A' : deltas.authorityLoad > 0 ? 'B' : 'tie'
    },
    
    evaluated_at: new Date().toISOString()
  };
}

/**
 * Calculate effectiveness score
 * Higher score = better intervention
 * Formula: (exposure reduction + cascade containment) - (stress increase * 0.5) - (commit cost * 2) - (authority load)
 */
function calculateEffectivenessScore(effects) {
  const {
    exposureReduction = 0,
    cascadeContainment = 0,
    stressIncrease = 0,
    commitCost = 0,
    decisionBacklogEffect = 0,
    authorityLoad = 0
  } = effects;
  
  return Math.round(
    exposureReduction + 
    cascadeContainment * 0.8 - 
    stressIncrease * 0.5 - 
    commitCost * 2 - 
    decisionBacklogEffect * 1.5 - 
    authorityLoad
  );
}

// ============================================================================
// PRE-BUILT COMPARISON TEMPLATES
// Common intervention comparison scenarios
// ============================================================================

export const COMPARISON_TEMPLATES = {
  freeze_vs_monitoring: {
    id: 'freeze_vs_monitoring',
    name: 'Merchant Freeze vs Enhanced Monitoring',
    description: 'Compare aggressive freeze intervention against enhanced monitoring approach',
    interventionA: INTERVENTION_TYPES.MERCHANT_FREEZE,
    interventionB: INTERVENTION_TYPES.MERCHANT_ENHANCED_MONITORING
  },
  
  throttle_vs_review: {
    id: 'throttle_vs_review',
    name: 'Beneficiary Throttling vs Regional Review',
    description: 'Compare beneficiary rate-limiting against regional review process',
    interventionA: INTERVENTION_TYPES.BENEFICIARY_THROTTLE,
    interventionB: INTERVENTION_TYPES.REGIONAL_REVIEW
  },
  
  local_vs_escalation: {
    id: 'local_vs_escalation',
    name: 'Local Authority Response vs Sponsor Escalation',
    description: 'Compare local immediate response against sponsor-level escalation',
    interventionA: INTERVENTION_TYPES.LOCAL_AUTHORITY_RESPONSE,
    interventionB: INTERVENTION_TYPES.SPONSOR_ESCALATION
  },
  
  threshold_vs_observation: {
    id: 'threshold_vs_observation',
    name: 'Threshold Tightening vs Continued Observation',
    description: 'Compare proactive threshold tightening against wait-and-see approach',
    interventionA: INTERVENTION_TYPES.THRESHOLD_TIGHTENING,
    interventionB: INTERVENTION_TYPES.CONTINUED_OBSERVATION
  }
};

// ============================================================================
// CONTEXT
// ============================================================================

const InterventionComparisonContext = createContext(null);

export function InterventionComparisonProvider({ children }) {
  // Comparison state
  const [comparisons, setComparisons] = useState([]);
  const [activeComparison, setActiveComparison] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(new Map());
  const [isEvaluating, setIsEvaluating] = useState(false);
  
  // Engine inputs (set by integration - derived from canonical contexts)
  const [engineInputs, setEngineInputs] = useState({
    stressIndex: null,
    exposureScore: null,
    cascadeRisk: null,
    decisionBacklog: null,
    commitPressure: null
  });
  
  /**
   * Update engine inputs from other contexts
   */
  const updateEngineInputs = useCallback((inputs) => {
    setEngineInputs(prev => ({ ...prev, ...inputs }));
  }, []);
  
  /**
   * Create comparison from template
   */
  const createFromTemplate = useCallback((templateId, options = {}) => {
    const template = COMPARISON_TEMPLATES[templateId];
    if (!template) return null;
    
    const comparison = createComparisonConfig({
      name: options.name || template.name,
      description: options.description || template.description,
      scenarioRef: options.scenarioRef,
      interventionA: template.interventionA,
      interventionB: template.interventionB,
      baselineState: options.baselineState || engineInputs
    });
    
    setComparisons(prev => [...prev, comparison]);
    return comparison;
  }, [engineInputs]);
  
  /**
   * Create custom comparison
   */
  const createCustomComparison = useCallback((config) => {
    const comparison = createComparisonConfig({
      ...config,
      baselineState: config.baselineState || engineInputs
    });
    setComparisons(prev => [...prev, comparison]);
    return comparison;
  }, [engineInputs]);
  
  /**
   * Evaluate a comparison
   */
  const evaluateComparison = useCallback((comparisonId) => {
    const comparison = comparisons.find(c => c.comparison_id === comparisonId);
    if (!comparison) return null;
    
    setIsEvaluating(true);
    setActiveComparison(comparison);
    
    // Update comparison status
    setComparisons(prev => prev.map(c => 
      c.comparison_id === comparisonId 
        ? { ...c, status: COMPARISON_STATUS.EVALUATED }
        : c
    ));
    
    const result = compareInterventions(comparison, engineInputs);
    
    // Store result
    setComparisonResults(prev => new Map(prev).set(comparisonId, result));
    
    // Update comparison with results
    setComparisons(prev => prev.map(c => 
      c.comparison_id === comparisonId 
        ? { ...c, results: result, evaluated_at: new Date().toISOString() }
        : c
    ));
    
    setIsEvaluating(false);
    return result;
  }, [comparisons, engineInputs]);
  
  /**
   * Delete a comparison
   */
  const deleteComparison = useCallback((comparisonId) => {
    setComparisons(prev => prev.filter(c => c.comparison_id !== comparisonId));
    setComparisonResults(prev => {
      const newMap = new Map(prev);
      newMap.delete(comparisonId);
      return newMap;
    });
    if (activeComparison?.comparison_id === comparisonId) {
      setActiveComparison(null);
    }
  }, [activeComparison]);
  
  /**
   * Get intervention definition
   */
  const getInterventionDefinition = useCallback((interventionType) => {
    return INTERVENTION_EFFECTS[interventionType] || null;
  }, []);
  
  /**
   * Get all available intervention types
   */
  const getAvailableInterventions = useCallback((category = null) => {
    const interventions = Object.entries(INTERVENTION_EFFECTS).map(([type, def]) => ({
      type,
      ...def
    }));
    
    if (!category) return interventions;
    return interventions.filter(i => i.category === category);
  }, []);
  
  // Computed values
  const evaluatedComparisons = useMemo(() => 
    comparisons.filter(c => c.status === COMPARISON_STATUS.EVALUATED),
  [comparisons]);
  
  const comparisonsByCategory = useMemo(() => {
    const grouped = {};
    comparisons.forEach(c => {
      const catA = c.intervention_a?.category || 'uncategorized';
      if (!grouped[catA]) grouped[catA] = [];
      grouped[catA].push(c);
    });
    return grouped;
  }, [comparisons]);
  
  const value = {
    // Constants
    INTERVENTION_TYPES,
    INTERVENTION_CATEGORIES,
    COMPARISON_STATUS,
    COMPARISON_TEMPLATES,
    
    // State
    comparisons,
    activeComparison,
    comparisonResults,
    isEvaluating,
    engineInputs,
    
    // Computed
    evaluatedComparisons,
    comparisonsByCategory,
    
    // Actions
    updateEngineInputs,
    createFromTemplate,
    createCustomComparison,
    evaluateComparison,
    deleteComparison,
    setActiveComparison,
    
    // Utilities
    getInterventionDefinition,
    getAvailableInterventions,
    evaluateIntervention,
    compareInterventions,
    createComparisonConfig
  };
  
  return (
    <InterventionComparisonContext.Provider value={value}>
      {children}
    </InterventionComparisonContext.Provider>
  );
}

export function useInterventionComparison() {
  const context = useContext(InterventionComparisonContext);
  if (!context) {
    throw new Error('useInterventionComparison must be used within InterventionComparisonProvider');
  }
  return context;
}

export default InterventionComparisonContext;
